import type { WikiConflict as Conflict } from "../wiki-types";
import { Button } from "@softmax/design-system";
import { styles } from "../wiki.styles";
import * as stylex from "@stylexjs/stylex";
import {
  spacing,
  colors,
  fonts,
  fontSizes,
  radii,
} from "@softmax/design-system/tokens.stylex";

export function WikiConflict({
  conflict,
  pending,
  canEdit,
  onMerge,
}: Readonly<{
  conflict: Conflict;
  pending: boolean;
  canEdit: boolean;
  onMerge(): void;
}>) {
  return (
    <section role="alert" {...stylex.props(styles.section)}>
      <h2 {...stylex.props(styles.sectionTitle, localStyles.conflictTitle)}>
        This page changed while you were editing
      </h2>
      <p {...stylex.props(styles.bodyText)}>
        Nothing was overwritten. Copy the changes you want into a fresh edit.
      </p>
      <div {...stylex.props(localStyles.conflictGrid)}>
        {[
          { title: "Your base", body: conflict.baseBody },
          {
            title: "Current revision",
            body: conflict.currentBody,
          },
          {
            title: "Your proposal",
            body: conflict.proposedBody,
          },
        ].map((pane) => (
          <section key={pane.title} {...stylex.props(localStyles.conflictPane)}>
            <h3 {...stylex.props(styles.eyebrow)}>{pane.title}</h3>
            <pre {...stylex.props(localStyles.conflictBody)}>{pane.body}</pre>
          </section>
        ))}
      </div>
      <div {...stylex.props(styles.actions)}>
        <Button
          variant="primary"
          onClick={onMerge}
          disabled={pending || !canEdit}
        >
          Start a fresh merge
        </Button>
      </div>
    </section>
  );
}
const localStyles = stylex.create({
  conflictTitle: { color: colors.danger },
  conflictGrid: {
    display: "grid",
    gridTemplateColumns: {
      default: "minmax(0, 1fr)",
      "@media (min-width: 1024px)": "repeat(3, minmax(0, 1fr))",
    },
    gap: spacing.space12,
  },
  conflictPane: {
    minWidth: 0,
    padding: spacing.space16,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: colors.border,
    borderRadius: radii.medium,
    backgroundColor: colors.surfaceAlt,
  },
  conflictBody: {
    margin: 0,
    marginTop: spacing.space8,
    maxHeight: "20rem",
    overflow: "auto",
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    fontSize: fontSizes.caption,
    fontFamily: fonts.mono,
    color: colors.subtle,
    lineHeight: 1.6,
  },
});
